import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Spinner } from './shared';

function formatUptime(startedAt) {
    const start = new Date(startedAt);
    const now = new Date();
    const diffMs = now - start;
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

function formatTime(timestamp) {
    if (!timestamp) return '-';
    const d = new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp);
    return d.toLocaleTimeString();
}

function MetricBar({ label, value, max, unit = '' }) {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    const color = pct > 80 ? 'bg-red-500' : pct > 60 ? 'bg-yellow-500' : 'bg-blue-500';
    return (
        <div className="flex items-center gap-3 text-sm">
            <span className="w-20 text-gray-500 shrink-0">{label}</span>
            <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="w-20 text-right text-gray-700 font-mono">{value}{unit}</span>
        </div>
    );
}


export default function AdminNodeDetail() {
    const { pid } = useParams();
    const [node, setNode] = useState(null);
    const [resolvedCache, setResolvedCache] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [nodesRes, resolvedRes] = await Promise.all([
                    adminApi.getMonitorNodes(),
                    adminApi.getResolvedCache(),
                ]);
                const found = nodesRes.nodes?.find(n => String(n.pid) === String(pid));
                setNode(found || null);
                setResolvedCache(resolvedRes);
            } catch (e) {
                console.error('Failed to load node detail:', e);
            } finally {
                setLoading(false);
            }
        })();
    }, [pid]);

    if (loading) {
        return (
            <div className="flex justify-center py-20">
                <Spinner size="lg" text="Loading node detail..." />
            </div>
        );
    }

    if (!node) {
        return (
            <div className="text-center py-20 text-gray-500">
                <p className="text-lg font-medium">Worker PID {pid} not found</p>
                <Link to="/admin/monitor" className="text-sm text-blue-600 hover:text-blue-800 mt-2 inline-block">Back to Monitor</Link>
            </div>
        );
    }

    const sys = node.system_stats || {};
    const cache = node.cache_stats || {};
    const sync = node.sync_stats || {};

    return (
        <div>
            <div className="flex items-center gap-3 mb-6">
                <Link to="/admin/monitor" className="text-gray-400 hover:text-gray-600">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                    </svg>
                </Link>
                <div>
                    <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-gray-900 text-white text-sm font-mono rounded">PID {node.pid}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            sync.connected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${sync.connected ? 'bg-green-500' : 'bg-red-500'}`} />
                            {sync.connected ? 'Connected' : 'Disconnected'}
                        </span>
                    </h1>
                    <p className="text-sm text-gray-500 mt-0.5">Uptime: {formatUptime(node.started_at)}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">System</p>
                    {Object.keys(sys).length > 0 ? (
                        <>
                            <MetricBar label="CPU" value={sys.cpu_percent || 0} max={100} unit="%" />
                            <MetricBar label="RAM" value={sys.memory_rss_mb || 0} max={1024} unit=" MB" />
                            <div className="flex items-center gap-6 text-sm pt-1">
                                <div><span className="text-gray-500">Threads</span> <span className="font-mono text-gray-900 ml-1">{sys.threads || 0}</span></div>
                                <div><span className="text-gray-500">Mem %</span> <span className="font-mono text-gray-900 ml-1">{sys.memory_percent || 0}%</span></div>
                            </div>
                        </>
                    ) : (
                        <p className="text-xs text-gray-400">No system stats (psutil not installed)</p>
                    )}
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Cache Counts</p>
                    <div className="space-y-2">
                        {Object.entries(cache).map(([key, count]) => (
                            <div key={key} className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">{key}</span>
                                <span className="font-mono text-gray-900 bg-gray-50 px-2 py-0.5 rounded">{count}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Sync</p>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-500">Messages received</span>
                            <span className="font-mono text-gray-900">{sync.messages_received || 0}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Messages sent</span>
                            <span className="font-mono text-gray-900">{sync.messages_sent || 0}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Last received</span>
                            <span className="font-mono text-gray-900">{formatTime(sync.last_received_at)}</span>
                        </div>
                        {sync.channels?.length > 0 && (
                            <div className="pt-1 border-t border-gray-100">
                                <p className="text-gray-500 mb-1">Channels</p>
                                <div className="flex flex-wrap gap-1">
                                    {sync.channels.map(ch => (
                                        <span key={ch} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">{ch}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {resolvedCache && (() => {
                const stats = resolvedCache.stats || {};
                const perAgent = stats.per_agent || {};
                return (
                    <div className="space-y-6 mt-8">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">Resolved Agent Cache</h2>
                            <div className="flex gap-2 text-xs text-gray-500">
                                <span className="px-2 py-1 bg-gray-100 rounded font-mono">PID {resolvedCache.pid}</span>
                                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">{stats.resolved_configs || 0} configs</span>
                                <span className="px-2 py-1 bg-green-50 text-green-700 rounded">{stats.compiled_agents || 0} compiled</span>
                                <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded">{stats.total_size_mb || 0} MB</span>
                            </div>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-lg p-5">
                            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Cache Size</p>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <p className="text-2xl font-mono font-semibold text-gray-900">{stats.total_size_mb || 0} <span className="text-sm text-gray-400">MB</span></p>
                                    <p className="text-xs text-gray-500 mt-0.5">Total</p>
                                </div>
                                <div>
                                    <p className="text-lg font-mono text-gray-700">{((stats.configs_size_bytes || 0) / 1024).toFixed(1)} <span className="text-sm text-gray-400">KB</span></p>
                                    <p className="text-xs text-gray-500 mt-0.5">Resolved Configs</p>
                                </div>
                                <div>
                                    <p className="text-lg font-mono text-gray-700">{((stats.compiled_size_bytes || 0) / 1024 / 1024).toFixed(2)} <span className="text-sm text-gray-400">MB</span></p>
                                    <p className="text-xs text-gray-500 mt-0.5">Compiled Graphs</p>
                                </div>
                            </div>
                            {Object.keys(perAgent).length > 0 && (
                                <div className="mt-4 pt-3 border-t border-gray-100">
                                    <p className="text-xs text-gray-400 mb-2">Per Agent</p>
                                    <div className="space-y-1.5">
                                        {Object.entries(perAgent).map(([aid, info]) => (
                                            <div key={aid} className="flex items-center gap-3 text-xs">
                                                <span className="font-mono font-medium text-gray-700 w-32">{aid}</span>
                                                <span className="font-mono text-gray-500">{info.size_kb} KB</span>
                                                <div className="flex gap-1 ml-auto">
                                                    {Object.entries(info).filter(([k]) => k !== 'size_bytes' && k !== 'size_kb').map(([k, v]) => (
                                                        <span key={k} className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">{k}: {v}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {stats.compiled_keys?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {stats.compiled_keys.map(k => (
                                    <span key={k} className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded font-mono">{k}</span>
                                ))}
                            </div>
                        )}

                        {Object.entries(resolvedCache.entries || {}).map(([agentId, detail]) => (
                            <ResolvedCacheEntry key={agentId} agentId={agentId} detail={detail} />
                        ))}
                    </div>
                );
            })()}
        </div>
    );
}

function ResolvedCacheEntry({ agentId, detail }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-gray-50"
            >
                <svg className={`w-3.5 h-3.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span className="font-mono font-medium text-gray-900">{agentId}</span>
                <span className="text-xs text-gray-500">{detail.config?.agent_type}</span>
                <span className="text-xs font-mono text-gray-400">{detail.config?.model_name}</span>
                <div className="ml-auto flex gap-1.5">
                    {['skills', 'subagents', 'tools', 'reminders', 'knowledge', 'mcp_servers'].map(k => {
                        const arr = detail[k];
                        if (!Array.isArray(arr)) return null;
                        return <span key={k} className={`px-1.5 py-0.5 rounded text-xs ${arr.length > 0 ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>{k} {arr.length}</span>;
                    })}
                </div>
            </button>

            {open && (
                <div className="border-t border-gray-100 divide-y divide-gray-100">
                    {Object.entries(detail).map(([key, value]) => (
                        <AutoSection key={key} sectionKey={key} value={value} />
                    ))}
                </div>
            )}
        </div>
    );
}

function AutoSection({ sectionKey, value }) {
    const [open, setOpen] = useState(false);

    if (value === null || value === undefined) return null;

    if (Array.isArray(value)) {
        return (
            <div>
                <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-5 py-2.5 text-left hover:bg-gray-50">
                    <svg className={`w-3 h-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{sectionKey}</span>
                    <span className="text-xs text-gray-400 font-mono">{value.length} items</span>
                </button>
                {open && (
                    <div className="px-5 pb-3 space-y-1">
                        {value.length === 0 ? (
                            <p className="text-xs text-gray-400">empty</p>
                        ) : value.map((item, i) => (
                            <AutoItem key={item?.id || item?.name || i} item={item} />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    if (typeof value === 'object') {
        return (
            <div>
                <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-5 py-2.5 text-left hover:bg-gray-50">
                    <svg className={`w-3 h-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{sectionKey}</span>
                    <span className="text-xs text-gray-400 font-mono">{Object.keys(value).length} keys</span>
                </button>
                {open && (
                    <div className="px-5 pb-3">
                        <pre className="text-xs font-mono whitespace-pre-wrap text-gray-700 bg-gray-50 p-3 rounded max-h-96 overflow-auto">
                            {JSON.stringify(value, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        );
    }

    if (typeof value === 'string' && value.length > 200) {
        return (
            <div>
                <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-5 py-2.5 text-left hover:bg-gray-50">
                    <svg className={`w-3 h-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{sectionKey}</span>
                    <span className="text-xs text-gray-400 font-mono">{value.length} chars</span>
                </button>
                {open && (
                    <div className="px-5 pb-3">
                        <pre className="text-xs font-mono whitespace-pre-wrap text-gray-700 bg-gray-50 p-3 rounded max-h-96 overflow-auto">{value}</pre>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="flex gap-2 px-5 py-2 text-xs">
            <span className="text-gray-400 w-28 shrink-0 uppercase">{sectionKey}</span>
            <span className="text-gray-700 font-mono">{String(value)}</span>
        </div>
    );
}

function AutoItem({ item }) {
    const [open, setOpen] = useState(false);
    if (typeof item !== 'object' || item === null) {
        return <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded font-mono">{String(item)}</span>;
    }
    const id = item.id || item.name || '';
    const entries = Object.entries(item).filter(([k]) => k !== 'id' && k !== 'name');
    return (
        <div className="rounded border border-gray-100 bg-gray-50">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-gray-100">
                <svg className={`w-2.5 h-2.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span className="font-mono text-gray-600 truncate">{id}</span>
                {item.name && item.name !== item.id && <span className="text-gray-500 truncate">{item.name}</span>}
            </button>
            {open && (
                <div className="px-4 py-2 border-t border-gray-100 text-xs space-y-0.5 max-h-80 overflow-auto">
                    {entries.map(([k, v]) => (
                        <div key={k} className="flex gap-2">
                            <span className="text-gray-400 w-28 shrink-0">{k}</span>
                            <span className="text-gray-600 font-mono break-all whitespace-pre-wrap">
                                {typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v ?? '')}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function CountBadge({ label, count, enabled }) {
    if (!enabled) return <span className="px-1.5 py-0.5 bg-gray-100 text-gray-400 rounded text-xs line-through">{label}</span>;
    const color = count > 0 ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-400';
    return <span className={`px-1.5 py-0.5 rounded text-xs ${color}`}>{label} {count}</span>;
}

function InfoLine({ label, value, mono }) {
    return (
        <div className="flex gap-2">
            <span className="text-gray-400 w-28 shrink-0">{label}</span>
            <span className={`text-gray-700 ${mono ? 'font-mono' : ''}`}>{value || '-'}</span>
        </div>
    );
}

function ExpandableSection({ title, count, badge, children }) {
    const [open, setOpen] = useState(false);
    return (
        <div>
            <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-5 py-2.5 text-left hover:bg-gray-50">
                <svg className={`w-3 h-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</span>
                <span className="text-xs text-gray-400 font-mono">{count}</span>
                {badge && <span className="px-1.5 py-0.5 bg-red-50 text-red-500 rounded text-xs">{badge}</span>}
            </button>
            {open && <div className="px-5 pb-3">{children}</div>}
        </div>
    );
}

function ItemListSection({ title, data, renderItem }) {
    if (!data) return null;
    const items = data.items || [];
    return (
        <ExpandableSection title={title} count={`${data.count || items.length}`} badge={data.enabled === false && 'disabled'}>
            {items.length === 0 ? (
                <p className="text-xs text-gray-400">empty</p>
            ) : (
                <div className="space-y-1">{items.map(renderItem)}</div>
            )}
        </ExpandableSection>
    );
}

function DetailRow({ id, name, meta = [], detail = {} }) {
    const [open, setOpen] = useState(false);
    const entries = Object.entries(detail || {}).filter(([, v]) => v != null && v !== '');
    return (
        <div className="rounded border border-gray-100 bg-gray-50">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-gray-100">
                <svg className={`w-2.5 h-2.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span className="font-mono text-gray-600 truncate">{id}</span>
                {name && name !== id && <span className="text-gray-500 truncate">{name}</span>}
                {meta.length > 0 && <span className="ml-auto text-gray-400 shrink-0">{meta.join(' · ')}</span>}
            </button>
            {open && entries.length > 0 && (
                <div className="px-4 py-2 border-t border-gray-100 text-xs space-y-0.5">
                    {entries.map(([k, v]) => (
                        <div key={k} className="flex gap-2">
                            <span className="text-gray-400 w-24 shrink-0">{k}</span>
                            <span className="text-gray-600 font-mono break-all">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ReminderDetail({ reminder: r }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded border border-gray-100 bg-gray-50">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-gray-100">
                <svg className={`w-2.5 h-2.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <span className="font-mono text-gray-600">{r.id}</span>
                <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{r.trigger}</span>
                <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{r.inject_as}</span>
                <span className="text-gray-400">P{r.priority}</span>
                {r.group && <span className="px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded">{r.group}</span>}
                {r.wrap_tag && <span className="text-gray-400">&lt;{r.wrap_tag}&gt;</span>}
                <span className="text-gray-400 ml-auto">{r.conditions?.length || 0} cond</span>
            </button>
            {open && (
                <div className="px-4 py-2 border-t border-gray-100 text-xs space-y-2">
                    {r.conditions?.length > 0 && (
                        <div>
                            <span className="text-gray-400">conditions:</span>
                            <pre className="mt-0.5 font-mono text-gray-600 bg-white p-2 rounded">{JSON.stringify(r.conditions, null, 2)}</pre>
                        </div>
                    )}
                    <div>
                        <span className="text-gray-400">template:</span>
                        <pre className="mt-0.5 font-mono text-gray-600 bg-white p-2 rounded whitespace-pre-wrap max-h-40 overflow-auto">{r.template}</pre>
                    </div>
                </div>
            )}
        </div>
    );
}
